import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Beaker, AlertCircle, CheckCircle, TrendingUp, Plus, Edit, Droplet, Building2, Network, Bell } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import api from '../services/api';

const WaterQuality = () => {
  const [sensorData, setSensorData] = useState([]);
  const [assets, setAssets] = useState([]);
  const [areas, setAreas] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [selectedScheme, setSelectedScheme] = useState('all');
  const [selectedInfraType, setSelectedInfraType] = useState('all');
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('pH');
  
  // Modal states
  const [showAddSchemeModal, setShowAddSchemeModal] = useState(false);
  const [showEditSchemeModal, setShowEditSchemeModal] = useState(false);
  const [showAddInfraModal, setShowAddInfraModal] = useState(false);
  const [showEditInfraModal, setShowEditInfraModal] = useState(false);
  const [showEditReadingsModal, setShowEditReadingsModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [alertInfo, setAlertInfo] = useState(null);
  
  // Form states
  const [schemeFormData, setSchemeFormData] = useState({
    name: '',
    area_id: '',
    status: 'Active'
  });
  
  const [infraFormData, setInfraFormData] = useState({
    name: '',
    type: 'WTP',
    parent_area_id: '',
    location: { lat: 0, lon: 0 },
    status: 'Active'
  });

  const [readingsFormData, setReadingsFormData] = useState({
    pH: '',
    turbidity: '',
    chlorine: '',
    TDS: ''
  });

  const qualityParams = [
    { name: 'pH', min: 6.5, max: 8.5, unit: '', icon: 'pH' },
    { name: 'turbidity', min: 0, max: 5, unit: 'NTU', icon: 'TRB' },
    { name: 'chlorine', min: 0.2, max: 1.0, unit: 'mg/L', icon: 'CL' },
    { name: 'TDS', min: 100, max: 500, unit: 'mg/L', icon: 'TDS' }
  ];

  const infraTypes = [
    { value: 'all', label: 'All Infrastructure', icon: Network },
    { value: 'Reservoir', label: 'Reservoirs', icon: Droplet },
    { value: 'WTP', label: 'Water Treatment Plants', icon: Beaker },
    { value: 'OHT', label: 'Overhead Tanks', icon: Building2 },
    { value: 'DMA', label: 'DMA Zones', icon: Network }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assetsRes, areasRes, schemesRes] = await Promise.all([
        api.assets.getAll(),
        api.areas.getAll(),
        api.schemes.getAll()
      ]);
      
      setAssets(assetsRes.data);
      setAreas(areasRes.data);
      setSchemes(schemesRes.data);
      
      // Generate random sensor data for all quality assets
      const qualityAssets = assetsRes.data.filter(a => 
        ['WTP', 'Reservoir', 'OHT', 'DMA'].includes(a.type)
      );
      
      const generatedData = generateSensorData(qualityAssets);
      setSensorData(generatedData);
      
      if (qualityAssets.length > 0) {
        setSelectedAssetId(qualityAssets[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSensorData = (assets) => {
    const data = [];
    const now = new Date();
    
    assets.forEach((asset, assetIndex) => {
      // Generate 24 hours of data (hourly)
      for (let i = 0; i < 24; i++) {
        const timestamp = new Date(now - (23 - i) * 60 * 60 * 1000);
        
        // pH: 6.5-8.5 (with 20% chance of violation for first asset)
        const pHBase = assetIndex === 0 && Math.random() < 0.2 
          ? (Math.random() < 0.5 ? 6.0 : 9.0) // Below min or above max
          : 6.5 + Math.random() * 2; // Normal range
        data.push({
          id: `${asset.id}-pH-${i}`,
          asset_id: asset.id,
          metric: 'pH',
          value: pHBase.toFixed(2),
          timestamp: timestamp.toISOString()
        });
        
        // Turbidity: 0-5 NTU (with 15% chance of violation for first asset)
        const turbidityBase = assetIndex === 0 && Math.random() < 0.15
          ? 6 + Math.random() * 3 // Above max
          : Math.random() * 5; // Normal range
        data.push({
          id: `${asset.id}-turbidity-${i}`,
          asset_id: asset.id,
          metric: 'turbidity',
          value: turbidityBase.toFixed(2),
          timestamp: timestamp.toISOString()
        });
        
        // Chlorine: 0.2-1.0 mg/L (with 10% chance of violation for first asset)
        const chlorineBase = assetIndex === 0 && Math.random() < 0.1
          ? (Math.random() < 0.5 ? 0.05 : 1.3) // Below min or above max
          : 0.2 + Math.random() * 0.8; // Normal range
        data.push({
          id: `${asset.id}-chlorine-${i}`,
          asset_id: asset.id,
          metric: 'chlorine',
          value: chlorineBase.toFixed(2),
          timestamp: timestamp.toISOString()
        });
        
        // TDS: 100-500 mg/L (with 15% chance of violation for first asset)
        const tdsBase = assetIndex === 0 && Math.random() < 0.15
          ? 550 + Math.random() * 150 // Above max
          : 100 + Math.random() * 400; // Normal range
        data.push({
          id: `${asset.id}-TDS-${i}`,
          asset_id: asset.id,
          metric: 'TDS',
          value: tdsBase.toFixed(2),
          timestamp: timestamp.toISOString()
        });
      }
    });
    
    return data;
  };

  const getFilteredAssets = () => {
    let filtered = assets.filter(a => ['WTP', 'Reservoir', 'OHT', 'DMA'].includes(a.type));
    
    if (selectedInfraType !== 'all') {
      filtered = filtered.filter(a => a.type === selectedInfraType);
    }
    
    if (selectedScheme !== 'all') {
      // Get villages in this scheme
      const scheme = schemes.find(s => s.id === selectedScheme);
      if (scheme) {
        // Filter assets that belong to the scheme's area
        filtered = filtered.filter(a => a.parent_area_id === scheme.area_id);
      }
    }
    
    return filtered;
  };

  const selectedAsset = assets.find(a => a.id === selectedAssetId);
  const filteredAssets = getFilteredAssets();

  const getLatestReading = (assetId, metric) => {
    const readings = sensorData.filter(d => d.asset_id === assetId && d.metric === metric);
    if (readings.length === 0) return null;
    return readings.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  };

  const isWithinRange = (value, param) => {
    return value >= param.min && value <= param.max;
  };

  const getChartData = (assetId, metric) => {
    return sensorData
      .filter(d => d.asset_id === assetId && d.metric === metric)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map(d => ({
        time: new Date(d.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        value: parseFloat(d.value)
      }));
  };

  const getInfraIcon = (type) => {
    switch(type) {
      case 'Reservoir': return <Droplet size={16} className="text-blue-400" />;
      case 'WTP': return <Beaker size={16} className="text-purple-400" />;
      case 'OHT': return <Building2 size={16} className="text-green-400" />;
      case 'DMA': return <Network size={16} className="text-orange-400" />;
      default: return <Network size={16} className="text-gray-400" />;
    }
  };

  // Scheme CRUD handlers
  const handleAddScheme = async () => {
    try {
      const response = await api.schemes.create(schemeFormData);
      console.log('Scheme created:', response.data);
      setShowAddSchemeModal(false);
      setSchemeFormData({ name: '', area_id: '', status: 'Active' });
      await fetchData();
      alert('Scheme added successfully!');
    } catch (error) {
      console.error('Failed to create scheme:', error);
      alert('Failed to create scheme: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditScheme = (scheme) => {
    setEditingScheme(scheme);
    setSchemeFormData({
      name: scheme.name,
      area_id: scheme.area_id,
      status: scheme.status
    });
    setShowEditSchemeModal(true);
  };

  const handleUpdateScheme = async () => {
    try {
      await api.schemes.update(editingScheme.id, schemeFormData);
      setShowEditSchemeModal(false);
      setEditingScheme(null);
      await fetchData();
      alert('Scheme updated successfully!');
    } catch (error) {
      console.error('Failed to update scheme:', error);
      alert('Failed to update scheme: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Infrastructure CRUD handlers
  const handleAddInfra = async () => {
    try {
      const response = await api.assets.create(infraFormData);
      console.log('Infrastructure created:', response.data);
      setShowAddInfraModal(false);
      setInfraFormData({ name: '', type: 'WTP', parent_area_id: '', location: { lat: 0, lon: 0 }, status: 'Active' });
      await fetchData();
      alert('Infrastructure added successfully!');
    } catch (error) {
      console.error('Failed to create infrastructure:', error);
      alert('Failed to create infrastructure: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditInfra = (asset) => {
    setEditingAsset(asset);
    setInfraFormData({
      name: asset.name,
      type: asset.type,
      parent_area_id: asset.parent_area_id || '',
      location: asset.location || { lat: 0, lon: 0 },
      status: asset.status
    });
    setShowEditInfraModal(true);
  };

  const handleUpdateInfra = async () => {
    try {
      await api.assets.update(editingAsset.id, infraFormData);
      setShowEditInfraModal(false);
      setEditingAsset(null);
      await fetchData();
      alert('Infrastructure updated successfully!');
    } catch (error) {
      console.error('Failed to update infrastructure:', error);
      alert('Failed to update infrastructure: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Readings edit handler
  const handleEditReadings = (asset) => {
    const params = ['pH', 'turbidity', 'chlorine', 'TDS'];
    const readings = {};
    params.forEach(p => {
      const reading = getLatestReading(asset.id, p);
      readings[p] = reading ? reading.value : '';
    });
    setReadingsFormData(readings);
    setEditingAsset(asset);
    setShowEditReadingsModal(true);
  };

  const handleUpdateReadings = () => {
    // Update sensor data in state
    const updatedData = [...sensorData];
    const now = new Date();
    
    Object.keys(readingsFormData).forEach(metric => {
      const value = readingsFormData[metric];
      if (value) {
        // Find existing reading or add new one
        const existingIndex = updatedData.findIndex(
          d => d.asset_id === editingAsset.id && d.metric === metric
        );
        
        if (existingIndex >= 0) {
          // Update the latest reading
          const latestIndex = updatedData
            .map((d, idx) => d.asset_id === editingAsset.id && d.metric === metric ? idx : -1)
            .filter(idx => idx !== -1)
            .sort((a, b) => new Date(updatedData[b].timestamp) - new Date(updatedData[a].timestamp))[0];
          
          if (latestIndex !== undefined) {
            updatedData[latestIndex].value = parseFloat(value).toFixed(2);
            updatedData[latestIndex].timestamp = now.toISOString();
          }
        } else {
          // Add new reading
          updatedData.push({
            id: `${editingAsset.id}-${metric}-new`,
            asset_id: editingAsset.id,
            metric: metric,
            value: parseFloat(value).toFixed(2),
            timestamp: now.toISOString()
          });
        }
      }
    });
    
    setSensorData(updatedData);
    setShowEditReadingsModal(false);
    setEditingAsset(null);
    alert('Readings updated successfully!');
  };

  const villages = areas.filter(a => a.type === 'Village');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-20 h-20 mx-auto">
              <Beaker className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
          </div>
          <p className="text-cyan-400 font-semibold tracking-wide">ANALYZING WATER QUALITY...</p>
          <p className="text-gray-500 text-sm mt-2">Loading sensor data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="water-quality-module">
      {/* Filters with Add buttons */}
      <Card className="bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-cyan-500/20 border border-cyan-500/40">
              <Beaker size={14} className="text-cyan-400" />
            </div>
            <CardTitle className="text-lg font-semibold tracking-wide">WATER QUALITY MONITORING - FILTERS</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Scheme Filter */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-300">Select Scheme</label>
                <Button 
                  size="sm" 
                  onClick={() => setShowAddSchemeModal(true)}
                  className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30 text-white"
                >
                  <Plus size={14} className="mr-1" />
                  Add
                </Button>
              </div>
              <Select value={selectedScheme} onValueChange={setSelectedScheme}>
                <SelectTrigger className="bg-slate-900 border-cyan-500/30 text-white hover:border-cyan-500/50 transition-colors">
                  <SelectValue placeholder="All Schemes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Schemes</SelectItem>
                  {schemes.map(scheme => (
                    <SelectItem key={scheme.id} value={scheme.id}>
                      {scheme.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Infrastructure Type Filter */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-300">Infrastructure Type</label>
                <Button 
                  size="sm" 
                  onClick={() => setShowAddInfraModal(true)}
                  className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30 text-white"
                >
                  <Plus size={14} className="mr-1" />
                  Add
                </Button>
              </div>
              <Select value={selectedInfraType} onValueChange={setSelectedInfraType}>
                <SelectTrigger className="bg-slate-900 border-cyan-500/30 text-white hover:border-cyan-500/50 transition-colors">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  {infraTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Filter Summary */}
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
            <span>Showing {filteredAssets.length} monitoring point(s)</span>
            {(selectedScheme !== 'all' || selectedInfraType !== 'all') && (
              <button
                onClick={() => {
                  setSelectedScheme('all');
                  setSelectedInfraType('all');
                }}
                className="text-xs text-blue-400 underline"
              >
                Clear Filters
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Monitoring Point Selector as Dropdown */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Network size={20} />
            Select Monitoring Point ({filteredAssets.length} available)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAssets.length === 0 ? (
            <div className="text-center py-4 text-gray-400">
              No monitoring points available for the selected filters
            </div>
          ) : (
            <Select value={selectedAssetId} onValueChange={setSelectedAssetId}>
              <SelectTrigger className="bg-slate-900 border-cyan-500/30 hover:border-cyan-500/50 transition-colors text-white">
                <SelectValue placeholder="Select a monitoring point" />
              </SelectTrigger>
              <SelectContent>
                {filteredAssets.map(asset => (
                  <SelectItem key={asset.id} value={asset.id}>
                    <div className="flex items-center gap-2">
                      {getInfraIcon(asset.type)}
                      <span>{asset.name}</span>
                      <span className="text-xs text-gray-400">({asset.type})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Live Parameters */}
      {selectedAsset && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {qualityParams.map((param) => {
              const reading = getLatestReading(selectedAsset.id, param.name);
              const isNormal = reading ? isWithinRange(reading.value, param) : false;
              
              return (
                <Card key={param.name} className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-400 capitalize">{param.name}</p>
                        <p className="text-3xl font-bold mt-2">
                          {reading ? reading.value : '--'}
                          <span className="text-sm text-gray-400 ml-1">{param.unit}</span>
                        </p>
                      </div>
                      <div className={`p-3 rounded-full ${
                        isNormal ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {isNormal ? (
                          <CheckCircle className="text-green-400" size={24} />
                        ) : (
                          <AlertCircle className="text-red-400" size={24} />
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      Range: {param.min} - {param.max} {param.unit}
                    </div>
                    <button
                      onClick={() => setSelectedMetric(param.name)}
                      className="mt-3 text-xs text-blue-400 flex items-center gap-1"
                    >
                      <TrendingUp size={12} />
                      View Trend
                    </button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Trend Chart */}
          <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="capitalize">
                  {selectedMetric} Trend - {selectedAsset.name} - Last 24 Hours
                </CardTitle>
                {(() => {
                  const currentParam = qualityParams.find(p => p.name === selectedMetric);
                  const chartData = getChartData(selectedAsset.id, selectedMetric);
                  const violationsCount = chartData.filter(d => 
                    d.value < currentParam.min || d.value > currentParam.max
                  ).length;
                  
                  return violationsCount > 0 && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setAlertInfo({
                          asset: selectedAsset,
                          metric: selectedMetric,
                          violations: violationsCount,
                          param: currentParam
                        });
                        setShowAlertModal(true);
                      }}
                      className="bg-red-600 text-white border-0 hover:bg-red-700"
                    >
                      <Bell size={14} className="mr-1" />
                      Alert Stakeholder ({violationsCount} violations)
                    </Button>
                  );
                })()}
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex gap-2">
                {qualityParams.map((param) => (
                  <button
                    key={param.name}
                    onClick={() => setSelectedMetric(param.name)}
                    className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                      selectedMetric === param.name 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-slate-800 text-white border border-slate-600'
                    }`}
                  >
                    {param.name}
                  </button>
                ))}
              </div>
              
              {/* Permissible Range Info */}
              <div className="mb-4 p-3 bg-slate-800 rounded-lg border border-slate-700">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-400">Permissible Range:</span>
                    <span className="text-green-400 font-semibold">
                      {qualityParams.find(p => p.name === selectedMetric)?.min} - {qualityParams.find(p => p.name === selectedMetric)?.max} {qualityParams.find(p => p.name === selectedMetric)?.unit}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-600 rounded"></div>
                      <span className="text-gray-400">Within Range</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-600 rounded"></div>
                      <span className="text-gray-400">Out of Range</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getChartData(selectedAsset.id, selectedMetric)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="time" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Legend />
                  {/* Reference lines for permissible range */}
                  <ReferenceLine 
                    y={qualityParams.find(p => p.name === selectedMetric)?.max} 
                    stroke="#ef4444" 
                    strokeDasharray="3 3"
                    label={{ value: 'Max Limit', fill: '#ef4444', fontSize: 12 }}
                  />
                  <ReferenceLine 
                    y={qualityParams.find(p => p.name === selectedMetric)?.min} 
                    stroke="#ef4444" 
                    strokeDasharray="3 3"
                    label={{ value: 'Min Limit', fill: '#ef4444', fontSize: 12 }}
                  />
                  <Bar dataKey="value" name={selectedMetric}>
                    {getChartData(selectedAsset.id, selectedMetric).map((entry, index) => {
                      const currentParam = qualityParams.find(p => p.name === selectedMetric);
                      const isOutOfRange = entry.value < currentParam.min || entry.value > currentParam.max;
                      return (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={isOutOfRange ? '#ef4444' : '#3b82f6'} 
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Compliance Status with Edit */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle>Compliance Status - Selected Monitoring Point</CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedAsset ? (
            <div className="text-center py-8 text-gray-400">
              Please select a monitoring point to view compliance status
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-700">
                  <tr className="text-left">
                    <th className="pb-3 font-semibold">Asset</th>
                    <th className="pb-3 font-semibold">Type</th>
                    <th className="pb-3 font-semibold">pH</th>
                    <th className="pb-3 font-semibold">Turbidity</th>
                    <th className="pb-3 font-semibold">Chlorine</th>
                    <th className="pb-3 font-semibold">TDS</th>
                    <th className="pb-3 font-semibold">Status</th>
                    <th className="pb-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[selectedAsset].map((asset) => {
                    const params = ['pH', 'turbidity', 'chlorine', 'TDS'];
                    const readings = params.map(p => getLatestReading(asset.id, p));
                    const allCompliant = readings.every((r, i) => {
                      if (!r) return false;
                      const param = qualityParams.find(p => p.name === params[i]);
                      return isWithinRange(r.value, param);
                    });

                    return (
                      <tr key={asset.id} className="border-b border-slate-800">
                        <td className="py-3">{asset.name}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            {getInfraIcon(asset.type)}
                            <span>{asset.type}</span>
                          </div>
                        </td>
                        {params.map((p, idx) => {
                          const reading = readings[idx];
                          const param = qualityParams[idx];
                          const isNormal = reading ? isWithinRange(reading.value, param) : false;
                          
                          return (
                            <td key={p} className="py-3">
                              {reading ? (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  isNormal ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {reading.value}
                                </span>
                              ) : (
                                <span className="text-gray-500">--</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="py-3">
                          {allCompliant ? (
                            <span className="flex items-center gap-1 text-green-400">
                              <CheckCircle size={14} />
                              Compliant
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-400">
                              <AlertCircle size={14} />
                              Check
                            </span>
                          )}
                        </td>
                        <td className="py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleEditReadings(asset)}
                              className="bg-green-600 text-white border-0 hover:bg-green-700"
                            >
                              <Edit size={14} className="mr-1" />
                              Edit Readings
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleEditInfra(asset)}
                              className="bg-slate-700 text-white border-0 hover:bg-slate-600"
                            >
                              <Edit size={14} className="mr-1" />
                              Edit Info
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Scheme Modal */}
      <Dialog open={showAddSchemeModal} onOpenChange={setShowAddSchemeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Scheme</DialogTitle>
            <DialogDescription>Create a new water supply scheme</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Scheme Name *</Label>
              <Input
                value={schemeFormData.name}
                onChange={(e) => setSchemeFormData({...schemeFormData, name: e.target.value})}
                placeholder="Enter scheme name"
              />
            </div>
            <div className="space-y-2">
              <Label>Village *</Label>
              <Select 
                value={schemeFormData.area_id}
                onValueChange={(value) => setSchemeFormData({...schemeFormData, area_id: value})}
              >
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>
                  {villages.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={schemeFormData.status}
                onValueChange={(value) => setSchemeFormData({...schemeFormData, status: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSchemeModal(false)}>Cancel</Button>
            <Button onClick={handleAddScheme} className="bg-blue-600 hover:bg-blue-700">Save Scheme</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Scheme Modal */}
      <Dialog open={showEditSchemeModal} onOpenChange={setShowEditSchemeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Scheme</DialogTitle>
            <DialogDescription>Modify scheme details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Scheme Name *</Label>
              <Input
                value={schemeFormData.name}
                onChange={(e) => setSchemeFormData({...schemeFormData, name: e.target.value})}
                placeholder="Enter scheme name"
              />
            </div>
            <div className="space-y-2">
              <Label>Village *</Label>
              <Select 
                value={schemeFormData.area_id}
                onValueChange={(value) => setSchemeFormData({...schemeFormData, area_id: value})}
              >
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>
                  {villages.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={schemeFormData.status}
                onValueChange={(value) => setSchemeFormData({...schemeFormData, status: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSchemeModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateScheme} className="bg-blue-600 hover:bg-blue-700">Update Scheme</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Infrastructure Modal */}
      <Dialog open={showAddInfraModal} onOpenChange={setShowAddInfraModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Infrastructure</DialogTitle>
            <DialogDescription>Create a new monitoring point</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={infraFormData.name}
                onChange={(e) => setInfraFormData({...infraFormData, name: e.target.value})}
                placeholder="Enter infrastructure name"
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select 
                value={infraFormData.type}
                onValueChange={(value) => setInfraFormData({...infraFormData, type: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WTP">Water Treatment Plant</SelectItem>
                  <SelectItem value="Reservoir">Reservoir</SelectItem>
                  <SelectItem value="OHT">Overhead Tank</SelectItem>
                  <SelectItem value="DMA">DMA Zone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Village</Label>
              <Select 
                value={infraFormData.parent_area_id}
                onValueChange={(value) => setInfraFormData({...infraFormData, parent_area_id: value})}
              >
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>
                  {villages.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={infraFormData.location.lat}
                  onChange={(e) => setInfraFormData({...infraFormData, location: {...infraFormData.location, lat: parseFloat(e.target.value) || 0}})}
                  placeholder="Latitude"
                />
              </div>
              <div className="space-y-2">
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={infraFormData.location.lon}
                  onChange={(e) => setInfraFormData({...infraFormData, location: {...infraFormData.location, lon: parseFloat(e.target.value) || 0}})}
                  placeholder="Longitude"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={infraFormData.status}
                onValueChange={(value) => setInfraFormData({...infraFormData, status: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddInfraModal(false)}>Cancel</Button>
            <Button onClick={handleAddInfra} className="bg-blue-600 hover:bg-blue-700">Save Infrastructure</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Infrastructure Modal */}
      <Dialog open={showEditInfraModal} onOpenChange={setShowEditInfraModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Infrastructure</DialogTitle>
            <DialogDescription>Modify monitoring point details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={infraFormData.name}
                onChange={(e) => setInfraFormData({...infraFormData, name: e.target.value})}
                placeholder="Enter infrastructure name"
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select 
                value={infraFormData.type}
                onValueChange={(value) => setInfraFormData({...infraFormData, type: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WTP">Water Treatment Plant</SelectItem>
                  <SelectItem value="Reservoir">Reservoir</SelectItem>
                  <SelectItem value="OHT">Overhead Tank</SelectItem>
                  <SelectItem value="DMA">DMA Zone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Village</Label>
              <Select 
                value={infraFormData.parent_area_id}
                onValueChange={(value) => setInfraFormData({...infraFormData, parent_area_id: value})}
              >
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>
                  {villages.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={infraFormData.location.lat}
                  onChange={(e) => setInfraFormData({...infraFormData, location: {...infraFormData.location, lat: parseFloat(e.target.value) || 0}})}
                  placeholder="Latitude"
                />
              </div>
              <div className="space-y-2">
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={infraFormData.location.lon}
                  onChange={(e) => setInfraFormData({...infraFormData, location: {...infraFormData.location, lon: parseFloat(e.target.value) || 0}})}
                  placeholder="Longitude"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={infraFormData.status}
                onValueChange={(value) => setInfraFormData({...infraFormData, status: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditInfraModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateInfra} className="bg-blue-600 hover:bg-blue-700">Update Infrastructure</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Readings Modal */}
      <Dialog open={showEditReadingsModal} onOpenChange={setShowEditReadingsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Water Quality Readings</DialogTitle>
            <DialogDescription>Update current sensor readings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>pH (6.5 - 8.5)</Label>
              <Input
                type="number"
                step="0.01"
                value={readingsFormData.pH}
                onChange={(e) => setReadingsFormData({...readingsFormData, pH: e.target.value})}
                placeholder="Enter pH value"
              />
            </div>
            <div className="space-y-2">
              <Label>Turbidity (0 - 5 NTU)</Label>
              <Input
                type="number"
                step="0.01"
                value={readingsFormData.turbidity}
                onChange={(e) => setReadingsFormData({...readingsFormData, turbidity: e.target.value})}
                placeholder="Enter turbidity value"
              />
            </div>
            <div className="space-y-2">
              <Label>Chlorine (0.2 - 1.0 mg/L)</Label>
              <Input
                type="number"
                step="0.01"
                value={readingsFormData.chlorine}
                onChange={(e) => setReadingsFormData({...readingsFormData, chlorine: e.target.value})}
                placeholder="Enter chlorine value"
              />
            </div>
            <div className="space-y-2">
              <Label>TDS (100 - 500 mg/L)</Label>
              <Input
                type="number"
                step="0.01"
                value={readingsFormData.TDS}
                onChange={(e) => setReadingsFormData({...readingsFormData, TDS: e.target.value})}
                placeholder="Enter TDS value"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditReadingsModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateReadings} className="bg-green-600 hover:bg-green-700">Save Readings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Stakeholder Modal */}
      <Dialog open={showAlertModal} onOpenChange={setShowAlertModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle size={24} />
              Water Quality Alert - Immediate Action Required
            </DialogTitle>
            <DialogDescription>
              Parameter violations detected for {alertInfo?.asset?.name}
            </DialogDescription>
          </DialogHeader>
          {alertInfo && (
            <div className="space-y-4 py-4">
              {/* Alert Summary */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-red-900">Parameter:</span>
                    <span className="text-red-700 capitalize">{alertInfo.metric}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-red-900">Violations:</span>
                    <span className="text-red-700">{alertInfo.violations} out of 24 readings</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-red-900">Permissible Range:</span>
                    <span className="text-red-700">
                      {alertInfo.param.min} - {alertInfo.param.max} {alertInfo.param.unit}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-red-900">Location:</span>
                    <span className="text-red-700">{alertInfo.asset.name}</span>
                  </div>
                </div>
              </div>

              {/* Stakeholders to Alert */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Notify Responsible Stakeholders:</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Field Officer - {alertInfo.asset.name}</p>
                        <p className="text-sm text-gray-600">fieldofficer.southgoa@goa.gov.in</p>
                        <p className="text-sm text-gray-600">+91-9822-XXXXXX</p>
                      </div>
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                  </div>
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Department Head - Water Quality</p>
                        <p className="text-sm text-gray-600">wq.head@goa.gov.in</p>
                        <p className="text-sm text-gray-600">+91-832-2712066</p>
                      </div>
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                  </div>
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">District Collector - South Goa</p>
                        <p className="text-sm text-gray-600">dc.southgoa@goa.gov.in</p>
                        <p className="text-sm text-gray-600">+91-832-2712066</p>
                      </div>
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Alert Message Preview */}
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">Alert Message:</h4>
                <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                  <p className="text-sm text-gray-800 whitespace-pre-line">
                    🚨 <strong>URGENT: Water Quality Alert</strong>
                    
                    <strong>Location:</strong> {alertInfo.asset.name}
                    <strong>Parameter:</strong> {alertInfo.metric}
                    <strong>Issue:</strong> {alertInfo.violations} readings out of permissible range
                    <strong>Permissible Range:</strong> {alertInfo.param.min} - {alertInfo.param.max} {alertInfo.param.unit}
                    <strong>Status:</strong> Immediate action required
                    
                    Please investigate and take corrective action immediately.
                    
                    Timestamp: {new Date().toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertModal(false)}>Close</Button>
            <Button 
              onClick={() => {
                alert('Alert sent to all stakeholders via Email & SMS');
                setShowAlertModal(false);
              }} 
              className="bg-red-600 hover:bg-red-700"
            >
              <Bell size={14} className="mr-1" />
              Send Alert Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaterQuality;
